import React, { useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import AnalyticsTracker from "../services/analytics/AnalyticsTracker";
import { canLoadStoreContext, isAdminSession, isAuthenticated, isImpersonating } from "../services/auth";
import { MerchantController, UserController } from "../services/controllers/global";

import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import EditCupom from "../components/organisms/EditCupom";
import EditEmployee from "../components/organisms/EditEmployee";
import Line from "../components/organisms/Line";
import NewAccessProfile from "../components/organisms/NewAccessProfile";
import NewCupom from "../components/organisms/NewCupom";
import NewEmployee from "../components/organisms/NewEmployee";
import NewService from "../components/organisms/NewService";
import ServiceEdit from "../components/organisms/ServiceEdit";
import AgendamentoPage from "./Agendamento";
import AgendamentoVeterinarioPage from "./AgendamentoVeterinario";
import AdminImpersonation from "./AdminImpersonation";
import Assinaturas from "./Assinaturas";
import { Avaliacoes } from "./Avaliacoes";
import Checkin from "./Checkin";
import CheckinClinica from "./CheckinClinica";
import Clientes from "./Clientes";
import { AtendimentoClinica } from "./Clinica/Atendimento";
import { ReportsVaccinations } from "./Clinica/ReportsVaccinations";
import Configuracoes from "./Configuracoes";
import { Crm } from "./Crm";
import CupomPage from "./Cupom";
import Dashboard from "./Dashboard";
import CrmPetDiagnosis from "./Diagnosis/CrmPet";
import EditPacote from "./EditPacote";
import FilaAtendimento from "./FilaAtendimento";
import HistoryVaccination from "./HistoryVaccination";
import Login from "./Login";
import Logout from "./Logout";
import NovoCliente from "./NovoCliente";
import NovoPacotes from "./NovoPacote";
import Pacotes from "./Pacotes";
import PreAgendamento from "./PreAgendamento";
import Profissionais from "./Profissionais";
import Relatorio from "./Relatorio";
import ResumoCliente from "./ResumoCliente";
import { Schedule } from "./Schedule";
import { Hub } from "./Hub";
import AgendaInteligente from "./AgendaInteligente";
import CrmPet from "./CrmPet";
import Servicos from "./Servicos";
import Vacinas from "./Vacinas";
import Vendas from "./Vendas";
import SuportePage from "./Suporte";

function PrivateRoute({ component: Component, ...rest }) {
  const authenticationStatus = isAuthenticated();
  const adminWithoutStoreSession = isAdminSession() && !isImpersonating();
  const adminRoute = rest.path === "/admin";
  const shouldLoadStoreContext = canLoadStoreContext();
  const CMerchant = MerchantController();
  const CUser = UserController();

  useEffect(() => {
    if (!shouldLoadStoreContext || !canLoadStoreContext()) return;

    CMerchant.init();
    CUser.init();
  }, [shouldLoadStoreContext, CMerchant, CUser]);

  return (
    <Route
      {...rest}
      render={(props) =>
        authenticationStatus && adminWithoutStoreSession && !adminRoute ? (
          <Redirect to={{ pathname: "/admin", state: { from: props.location } }} />
        ) : authenticationStatus ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: "/", state: { from: props.location } }} />
        )
      }
    />
  );
}


function Routes() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <QueryParamProvider adapter={ReactRouter5Adapter}>
        <Switch>
          <PrivateRoute
            exact
            path="/admin"
            component={AdminImpersonation}
          />
          <PrivateRoute
            exact
            path="/pre-agendamento"
            component={PreAgendamento}
          />
          <PrivateRoute
            exact
            path="/fila-de-atendimento"
            component={FilaAtendimento}
          />
          <PrivateRoute
            exact
            path="/assinaturas"
            component={Assinaturas}
          />
          <PrivateRoute
            exact
            path="/dashboard"
            component={Dashboard}
          />
          <PrivateRoute
            exact
            path="/horario-trabalho"
            component={Line}
          />
          <PrivateRoute
            exact
            path="/banho-tosa/agendamento"
            component={AgendamentoPage}
          />
          <PrivateRoute
            exact
            path="/banho-tosa/agendamento/:data"
            component={AgendamentoPage}
          />
          <PrivateRoute
            exact
            path="/vacinas"
            component={HistoryVaccination}
          />
          <PrivateRoute
            exact
            path="/clinica/relatorios/vacinacoes"
            component={ReportsVaccinations}
          />
          <PrivateRoute
            exact
            path="/clinica/agendamento"
            component={AgendamentoVeterinarioPage}
          />
          <PrivateRoute
            exact
            path="/clinica/agendamento/:data"
            component={AgendamentoVeterinarioPage}
          />
          <PrivateRoute
            exact
            path="/clinica/check-in/order/:orderId"
            component={CheckinClinica}
          />
          <PrivateRoute
            exact
            path="/banho-tosa/check-in/order/:orderId"
            component={Checkin}
          />
          <PrivateRoute exact path="/clientes" component={Clientes} />
          <PrivateRoute
            exact
            path="/clientes/resumo/:id"
            component={ResumoCliente}
          />
          <PrivateRoute
            exact
            path="/clientes/novo"
            component={NovoCliente}
          />
          <PrivateRoute
            exact
            path="/avaliacoes"
            component={Avaliacoes}
          />
          <PrivateRoute exact path="/crm" component={Crm} />
          <PrivateRoute
            exact
            path="/packages"
            component={Pacotes}
          />
          <PrivateRoute
            exact
            path="/pacotes"
            component={Pacotes}
          />
          <PrivateRoute
            exact
            path="/relatorio"
            component={Relatorio}
          />
          <PrivateRoute
            exact
            path="/cupom"
            component={CupomPage}
          />

          <PrivateRoute
            exact
            path="/cupom/editar/:id"
            component={EditCupom}
          />

          <PrivateRoute
            exact
            path="/cupom/new"
            component={NewCupom}
          />
          <PrivateRoute
            exact
            path="/pacotes/new"
            component={NovoPacotes}
          />
          <PrivateRoute
            exact
            path="/pacotes/vendas"
            component={Vendas}
          />
          <PrivateRoute
            exact
            path="/pacotes/edit/:id"
            component={EditPacote}
          />
          <PrivateRoute
            exact
            path="/profissionais"
            component={Profissionais}
          />
          <PrivateRoute exact path="/protocolos" component={Vacinas} />
          <PrivateRoute
            exact
            path="/profissionais/new"
            component={NewEmployee}
          />
          <PrivateRoute
            exact
            path="/profissionais/editar/:id"
            component={EditEmployee}
          />
          <PrivateRoute
            exact
            path="/profissionais/profile/new"
            component={NewAccessProfile}
          />
          <PrivateRoute
            exact
            path="/servicos"
            component={Servicos}
          />
          <PrivateRoute
            exact
            path="/servicos/new"
            component={NewService}
          />
          <PrivateRoute
            exact
            path="/servicos/editar/:edit"
            component={ServiceEdit}
          />

          <PrivateRoute
            exact
            path="/configuracoes"
            component={Configuracoes}
          />
          {/* ************************************ */}
          {/* New Paths */}
          {/* ************************************ */}

          <PrivateRoute
            exact
            path="/clinica/atendimento/:orderId"
            component={AtendimentoClinica}
          />

          <PrivateRoute
            exact
            path="/agenda"
            component={Schedule}
          />
          <PrivateRoute
            exact
            path="/agenda-inteligente"
            component={AgendaInteligente}
          />
          <PrivateRoute
            exact
            path="/vendas/crm-pet"
            component={CrmPet}
          />
          <PrivateRoute
            exact
            path="/diagnosis/crm-pet"
            component={CrmPetDiagnosis}
          />
          <PrivateRoute
            exact
            path="/hub"
            component={Hub}
          />

          <PrivateRoute
            exact
            path="/suporte"
            component={SuportePage}
          />

          <Route exact path="/" component={Login} />
          <Route exact path="/logout" component={Logout} />
          <Route path="*" component={Login} />
        </Switch>
      </QueryParamProvider>
    </BrowserRouter>
  );
}

export default Routes;
